import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { useTranslation } from 'react-i18next';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { reportError } from '../../lib/sentry';
import FlashSnackbar, { Severity } from '../common/FlashSnackbar';
import openOverviewPatientForm from '../../lib/services/adminFormService';
import {
  getNewVisit,
  getAllFollowUps,
  getNewVisitStatus,
  reopenNewVisit,
  setNewVisitVisibility,
  NewVisitForm,
  NewVisitFormOverride,
  NewVisitFormStatus,
  FollowUpForm,
} from '../../lib/services/formService';
import NewVisitJournal from './NewVisitJournal';
import FollowUpJournal from './FollowUpJournal';
import { hasStatus } from '../../lib/services/httpError';
import { DS, tealButtonSx } from './designTokens';
import { statusColor } from './formUtils';

interface FormOverviewProps {
  patient: {
    _id: string;
    firstName?: string;
    lastName?: string;
    personalNumber?: string;
  };
}

type SubView =
  | { type: 'overview' }
  | { type: 'new-visit' }
  | { type: 'followup'; formId: string };

const formatRowDate = (form: NewVisitForm | FollowUpForm): string => {
  if (form.status === 'submitted' && form.submittedAt) {
    const date = new Date(form.submittedAt).toLocaleString('sv-SE', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    return `Inskickad (${date})`;
  }
  if (form.status === 'inProgress' && 'updatedAt' in form && form.updatedAt) {
    const date = new Date(form.updatedAt).toLocaleString('sv-SE', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    return `Påbörjad (${date})`;
  }
  return 'Ej påbörjad';
};

const sectionLabelSx = {
  fontSize: '12px',
  fontWeight: 900,
  color: DS.inkSecondary,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.08em',
  mb: 1.5,
};

const cardSx = {
  background: DS.surfaceRaised,
  borderRadius: DS.radiusLg,
  boxShadow: DS.shadowSm,
  overflow: 'hidden',
};

const visibilityStatusKey = (
  status: NewVisitFormStatus,
  hasForm: boolean
): string => {
  if (status.status === 'submitted') return 'formVisibility.lockedBySubmission';
  if (!status.shouldShowForm) return 'formVisibility.notShown';
  return hasForm ? 'formVisibility.shown' : 'formVisibility.awaitingPatient';
};

const toggleGroupSx = {
  '& .MuiToggleButton-root': {
    fontFamily: DS.fontBody,
    fontWeight: 700,
    fontSize: '12px',
    textTransform: 'none' as const,
    color: DS.inkSecondary,
    borderColor: DS.hairline,
    px: 1.75,
    py: 0.5,
  },
  '& .MuiToggleButton-root.Mui-selected': {
    color: DS.surfaceRaised,
    backgroundColor: DS.deepTeal,
    '&:hover': { backgroundColor: DS.deepTeal },
  },
  '& .MuiToggleButton-root.Mui-selected.Mui-disabled': {
    color: DS.surfaceRaised,
    backgroundColor: DS.muted,
    '&:hover': { backgroundColor: DS.muted },
  },
};

const effectTextSx = { fontSize: '13px', color: DS.inkSecondary };

const smallTealButtonSx = {
  ...tealButtonSx,
  fontSize: '13px',
  px: 2,
  py: 0.5,
  letterSpacing: '0.05em',
};

const FormOverview: React.FC<FormOverviewProps> = ({ patient }) => {
  const [loading, setLoading] = useState(true);
  const [initialForm, setInitialForm] = useState<NewVisitForm | null>(null);
  const [followupForms, setFollowupForms] = useState<FollowUpForm[]>([]);
  const [subView, setSubView] = useState<SubView>({ type: 'overview' });
  const [showConfirm, setShowConfirm] = useState(false);
  const [flash, setFlash] = useState<{
    msg: string;
    severity: Severity;
  } | null>(null);
  const [visibility, setVisibility] = useState<NewVisitFormStatus | null>(null);
  const [savingVisibility, setSavingVisibility] = useState(false);
  const [confirmHide, setConfirmHide] = useState(false);
  const { t } = useTranslation();

  const loadVisibility = useCallback(async () => {
    try {
      const res = await getNewVisitStatus(patient._id);
      setVisibility(res.data.result);
    } catch (err) {
      reportError(err);
      setVisibility(null);
    }
  }, [patient._id]);

  useEffect(() => {
    const load = async () => {
      try {
        const [newVisitRes, followUpsRes] = await Promise.all([
          getNewVisit(patient._id),
          getAllFollowUps(patient._id),
        ]);

        setInitialForm(newVisitRes.data.result.form);

        const forms = followUpsRes.data.result.forms || [];
        const sorted = [...forms].sort((a, b) => {
          if (a.status === 'inProgress') return -1;
          if (b.status === 'inProgress') return 1;
          const dateA = new Date(a.submittedAt || '').getTime();
          const dateB = new Date(b.submittedAt || '').getTime();
          return dateB - dateA;
        });
        setFollowupForms(sorted);
      } catch (err) {
        reportError(err, { tags: { area: 'forms_overview' } });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [patient._id]);

  useEffect(() => {
    loadVisibility();
  }, [loadVisibility]);

  const visibilityLocked = visibility?.status === 'submitted';

  const handleVisibility = async (override: NewVisitFormOverride | null) => {
    setSavingVisibility(true);
    try {
      await setNewVisitVisibility(patient._id, override);
      await loadVisibility();
      setFlash({ msg: t('formVisibility.updated'), severity: 'success' });
    } catch (err) {
      const conflict = hasStatus(err, 409);
      if (!conflict) reportError(err);
      setFlash({
        msg: conflict
          ? t('formVisibility.useReopen')
          : t('formVisibility.updateFailed'),
        severity: 'error',
      });
    } finally {
      setSavingVisibility(false);
    }
  };

  const handleReopen = async () => {
    try {
      const res = await reopenNewVisit(patient._id);
      setInitialForm(res.data.result.form);
      await loadVisibility();
      setFlash({
        msg: 'Formuläret är nu upplåst för patienten.',
        severity: 'success',
      });
    } catch (err) {
      reportError(err, { tags: { area: 'reopen_new_visit' } });
      setFlash({ msg: 'Kunde inte låsa upp formuläret.', severity: 'error' });
    } finally {
      setShowConfirm(false);
    }
  };

  if (subView.type === 'new-visit') {
    return (
      <NewVisitJournal
        patient={patient}
        onBack={() => setSubView({ type: 'overview' })}
      />
    );
  }

  if (subView.type === 'followup') {
    return (
      <FollowUpJournal
        patient={patient}
        formId={subView.formId}
        onBack={() => setSubView({ type: 'overview' })}
      />
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: DS.deepTeal }} />
      </Box>
    );
  }

  return (
    <Box sx={{ fontFamily: DS.fontBody, maxWidth: 720 }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontFamily: DS.fontDisplay,
            fontWeight: 600,
            fontSize: '24px',
            color: DS.softBlack,
            mb: 1,
          }}
        >
          Formuläröversikt
        </Typography>
      </Box>

      {followupForms.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={sectionLabelSx}>Återbesöksformulär</Typography>
          <Box sx={cardSx}>
            {followupForms.map((form, index) => (
              <Box
                key={form._id || form.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  px: '20px',
                  py: '14px',
                  borderTop: index > 0 ? `1px solid ${DS.hairline}` : 'none',
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: statusColor(form.status),
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{ flex: 1, fontSize: '14px', color: DS.softBlack }}
                >
                  {formatRowDate(form)}
                </Typography>
                <Button
                  size="small"
                  onClick={() =>
                    setSubView({
                      type: 'followup',
                      formId: String(form._id || form.id),
                    })
                  }
                  sx={smallTealButtonSx}
                >
                  Öppna
                </Button>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Typography sx={{ ...sectionLabelSx, mb: 0, flex: 1 }}>
            Nybesöksformulär
          </Typography>
          {visibility && (
            <Typography sx={effectTextSx}>
              {t('formVisibility.controlLabel')}
            </Typography>
          )}
          {visibility && (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={visibilityLocked ? null : (visibility.override ?? 'auto')}
              onChange={(_event, next) => {
                if (!next) return;
                if (next === 'hide') {
                  setConfirmHide(true);
                  return;
                }
                handleVisibility(next === 'auto' ? null : next);
              }}
              sx={toggleGroupSx}
            >
              <ToggleButton
                value="auto"
                disabled={savingVisibility || visibilityLocked}
              >
                {t('formVisibility.modeAuto')}
              </ToggleButton>
              <ToggleButton
                value="show"
                disabled={savingVisibility || visibilityLocked}
              >
                {t('formVisibility.modeShow')}
              </ToggleButton>
              <ToggleButton
                value="hide"
                disabled={savingVisibility || visibilityLocked}
              >
                {t('formVisibility.modeHide')}
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>
        {visibility && (
          <Typography sx={{ ...effectTextSx, pb: 1 }}>
            {t(visibilityStatusKey(visibility, !!initialForm))}
          </Typography>
        )}
        {initialForm && (
          <Box sx={cardSx}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                px: '20px',
                py: '14px',
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: statusColor(initialForm.status),
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{ flex: 1, fontSize: '14px', color: DS.softBlack }}
              >
                {formatRowDate(initialForm)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  onClick={() => setSubView({ type: 'new-visit' })}
                  sx={smallTealButtonSx}
                >
                  Öppna
                </Button>
                {initialForm.status === 'submitted' && (
                  <Button
                    size="small"
                    startIcon={
                      <LockOpenIcon sx={{ fontSize: '14px !important' }} />
                    }
                    onClick={() => setShowConfirm(true)}
                    sx={{
                      fontFamily: DS.fontBody,
                      fontWeight: 700,
                      fontSize: '13px',
                      color: DS.amberDark,
                      backgroundColor: DS.amberLight,
                      border: `1px solid ${DS.amberBorder}`,
                      borderRadius: '999px',
                      px: 2,
                      py: 0.5,
                      textTransform: 'none',
                      '&:hover': { backgroundColor: DS.amberHover },
                    }}
                  >
                    Lås upp
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          mt: 4,
          pt: 2,
          borderTop: `1px solid ${DS.hairline}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Button
          size="small"
          startIcon={<OpenInNewIcon sx={{ fontSize: '13px !important' }} />}
          onClick={() =>
            openOverviewPatientForm({
              personalNumber: patient.personalNumber ?? '',
              firstName: patient.firstName ?? '',
              lastName: patient.lastName ?? '',
            })
          }
          sx={{
            fontFamily: DS.fontBody,
            textTransform: 'none',
            fontSize: '12px',
            color: DS.inkSecondary,
            px: 1,
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' },
          }}
        >
          Öppna i externt system
        </Button>
        <Typography
          sx={{
            fontSize: '11px',
            color: DS.inkSecondary,
            opacity: 0.7,
            fontFamily: DS.fontBody,
          }}
        >
          Saknas data? Använd den externa formulärtjänsten som reserv.
        </Typography>
      </Box>

      <Dialog
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        PaperProps={{ sx: { borderRadius: '30px', p: 1 } }}
      >
        <DialogTitle sx={{ fontFamily: DS.fontDisplay, fontWeight: 600 }}>
          Lås upp formulär
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{ fontFamily: DS.fontBody, color: DS.softBlack }}
          >
            Är du säker på att du vill låsa upp formuläret så att patienten kan
            fylla i det igen?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setShowConfirm(false)}
            sx={{
              fontFamily: DS.fontBody,
              fontWeight: 700,
              color: DS.softBlack,
              borderRadius: '999px',
              textTransform: 'none',
            }}
          >
            Avbryt
          </Button>
          <Button onClick={handleReopen} sx={{ ...tealButtonSx, px: 3 }}>
            Bekräfta
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmHide}
        onClose={() => setConfirmHide(false)}
        PaperProps={{ sx: { borderRadius: '30px', p: 1 } }}
      >
        <DialogTitle sx={{ fontFamily: DS.fontDisplay, fontWeight: 600 }}>
          {t('formVisibility.hideConfirmTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{ fontFamily: DS.fontBody, color: DS.softBlack }}
          >
            {t('formVisibility.hideConfirmBody')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmHide(false)}
            sx={{
              fontFamily: DS.fontBody,
              fontWeight: 700,
              color: DS.softBlack,
              borderRadius: '999px',
              textTransform: 'none',
            }}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={() => {
              setConfirmHide(false);
              handleVisibility('hide');
            }}
            sx={{ ...tealButtonSx, px: 3 }}
          >
            {t('formVisibility.hideConfirmButton')}
          </Button>
        </DialogActions>
      </Dialog>

      <FlashSnackbar
        open={!!flash}
        msg={flash?.msg || ''}
        severity={flash?.severity}
        onClose={() => setFlash(null)}
      />
    </Box>
  );
};

export default FormOverview;
